import { apiClient } from './api-client';

/**
 * B6.15 — issued-revision storage accounting and reconciliation (Owner/Admin).
 *
 * Read-only: every method is a GET, because nothing behind them writes.
 * Reconciliation is a GET for the same reason — it inspects storage and reports
 * what it found, and never deletes or repairs anything.
 *
 * `apiClient` unwraps the `{ data, meta }` envelope in its interceptor, so
 * `r.data` here is already the payload.
 */

export type RevisionFileKind = 'PHOTO' | 'BRANDING';

export interface RevisionFile {
  kind: RevisionFileKind;
  storageKey: string;
  mimeType: string;
  checksumSha256: string;
  /** Null for revisions approved before B6.15, which never recorded a size. */
  sizeBytes: number | null;
  position: number | null;
}

export interface RevisionStorage {
  revision: number;
  reportNumber: string | null;
  approvedAt: string;
  fileCount: number;
  totalSizeBytes: number;
  filesWithUnknownSize: number;
  files: RevisionFile[];
}

export interface ReportStorage {
  reportId: string;
  reportNumber: string | null;
  inspectionNumber: string | null;
  revisionCount: number;
  fileCount: number;
  totalSizeBytes: number;
  filesWithUnknownSize: number;
  revisions: RevisionStorage[];
}

export interface TenantStorageSummary {
  reportCount: number;
  revisionCount: number;
  fileCount: number;
  totalSizeBytes: number;
  filesWithUnknownSize: number;
  largestRevision: {
    reportId: string;
    reportNumber: string | null;
    revision: number;
    sizeBytes: number;
  } | null;
  reports: ReportStorage[];
}

export type ReconciliationFindingType =
  | 'OK'
  | 'MISSING_FILE'
  | 'SIZE_MISMATCH'
  | 'CHECKSUM_MISMATCH'
  | 'MISSING_BRANDING'
  | 'MISSING_PHOTO'
  | 'INCOMPLETE_REVISION'
  | 'ORPHANED_REVISION_STORAGE'
  | 'UNEXPECTED_NESTED_FILE'
  | 'UNREADABLE_FILE';

export interface ReconciliationFinding {
  type: ReconciliationFindingType;
  reportId: string | null;
  reportNumber: string | null;
  revision: number | null;
  storageKey: string;
  expectedSizeBytes: number | null;
  actualSizeBytes: number | null;
  expectedChecksum: string | null;
  actualChecksum: string | null;
  detail: string;
}

export interface ReconciliationResult {
  startedAt: string;
  completedAt: string;
  checksumsVerified: boolean;
  reportsScanned: number;
  revisionsScanned: number;
  filesExpected: number;
  filesVerified: number;
  issueCount: number;
  findings: ReconciliationFinding[];
}

const base = '/report-storage';

export const reportStorageService = {
  getSummary: (): Promise<TenantStorageSummary> =>
    apiClient.get<TenantStorageSummary>(`${base}/summary`).then((r) => r.data),

  getReport: (reportId: string): Promise<ReportStorage> =>
    apiClient.get<ReportStorage>(`${base}/reports/${reportId}`).then((r) => r.data),

  getRevision: (reportId: string, revision: number): Promise<RevisionStorage> =>
    apiClient
      .get<RevisionStorage>(`${base}/reports/${reportId}/revisions/${revision}`)
      .then((r) => r.data),

  reconcile: (verifyChecksums = false): Promise<ReconciliationResult> =>
    apiClient
      .get<ReconciliationResult>(`${base}/reconciliation`, { params: { verifyChecksums } })
      .then((r) => r.data),

  reconcileReport: (reportId: string, verifyChecksums = false): Promise<ReconciliationResult> =>
    apiClient
      .get<ReconciliationResult>(`${base}/reports/${reportId}/reconciliation`, {
        params: { verifyChecksums },
      })
      .then((r) => r.data),
};

/** Byte formatting shared by the storage screens. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  // One decimal below 100, none above — 329 KB reads better than 328.8 KB, and
  // 11.0 KB is more useful than 11 KB when comparing revisions.
  return `${value.toFixed(value >= 100 ? 0 : 1)} ${units[unit]}`;
}
